import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Clock, Save, RefreshCw, AlertTriangle, ShieldCheck, Info } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import {
  AU_TIMEZONE_VALUES,
  AU_TIMEZONE_LABELS,
  isAuTimezone,
} from '@valutten/shared-schemas/dist/leadTaxonomy';
import { functions, db } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { CalendarConnectionCard } from '../../components/leads/CalendarConnectionCard';

type OutletCtx = {
  userData?: {
    uid?: string;
    companyId?: string;
    role?: string;
    displayName?: string;
  };
};

interface DayWindow { start: string; end: string; }
type WeeklySchedule = Record<DayKey, DayWindow[]>;
type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const HOUR_RANGE = Array.from({ length: 17 }, (_, i) => i + 6); // 06:00..22:00

interface OutOfOffice {
  enabled: boolean;
  until: string | null;
  reason: string | null;
}

interface AvailabilityDoc {
  weeklySchedule: WeeklySchedule;
  timezone: string;
  outOfOffice: OutOfOffice;
  maxOpenLeads: number | null;
  acceptingNewLeads: boolean;
}

const EMPTY_SCHEDULE: WeeklySchedule = {
  mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
};
const DEFAULT_NINE_TO_FIVE: WeeklySchedule = {
  mon: [{ start: '09:00', end: '17:00' }],
  tue: [{ start: '09:00', end: '17:00' }],
  wed: [{ start: '09:00', end: '17:00' }],
  thu: [{ start: '09:00', end: '17:00' }],
  fri: [{ start: '09:00', end: '17:00' }],
  sat: [], sun: [],
};

// Preselected zone for a broker who has never saved. The device's own zone is
// only a starting value for a visible, editable select on a document that does
// not exist yet: nothing is persisted until the broker presses Save, and the
// router never guesses from it. Anything outside the Australian allow-list
// falls back to Sydney.
function detectDeviceTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isAuTimezone(zone) ? zone : 'Australia/Sydney';
  } catch {
    return 'Australia/Sydney';
  }
}

const DEFAULT_DOC: AvailabilityDoc = {
  weeklySchedule: DEFAULT_NINE_TO_FIVE,
  timezone: detectDeviceTimezone(),
  outOfOffice: { enabled: false, until: null, reason: null },
  maxOpenLeads: null, // null = inherit company default
  acceptingNewLeads: true,
};

/** Weekday key and HH for `at`, as seen in `timeZone`. */
function zonedDayAndHour(at: Date, timeZone: string): { dayKey: DayKey; hour: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(at);
    const weekday = parts.find((p) => p.type === 'weekday')?.value?.toLowerCase().slice(0, 3);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
    const dayKey = DAY_KEYS.find((d) => d === weekday) ?? 'mon';
    return { dayKey, hour: Number.isNaN(hour) ? 0 : hour };
  } catch {
    const dayIdx = (at.getDay() + 6) % 7; // Monday-based
    return { dayKey: DAY_KEYS[dayIdx], hour: at.getHours() };
  }
}

/**
 * The instant leave ends, mirroring the router's rule: a date-only value runs
 * to the END of that date in the broker's own zone, not UTC midnight.
 */
function leaveEndMs(until: string | null, timeZone: string): number | null {
  if (!until) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(until.trim());
  if (!m) {
    const parsed = new Date(until).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  // Start of the following day, in `timeZone`. Intl round-trip: read the
  // offset the zone had at roughly that instant and subtract it.
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1, 0, 0, 0, 0);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(guess));
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
    const seen = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'), 0);
    return guess - (seen - guess);
  } catch {
    return guess;
  }
}

// Convert HH:MM to/from a hour×day grid for click toggling.
// activeSet[`${dayKey}-${hh}`] = true if the broker is working that hour.
function scheduleToActiveSet(s: WeeklySchedule): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const day of DAY_KEYS) {
    for (const w of s[day] ?? []) {
      const startH = parseInt(w.start.split(':')[0], 10);
      const endH = parseInt(w.end.split(':')[0], 10);
      for (let h = startH; h < endH; h++) {
        out[`${day}-${h}`] = true;
      }
    }
  }
  return out;
}

function activeSetToSchedule(set: Record<string, boolean>): WeeklySchedule {
  const out: WeeklySchedule = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
  for (const day of DAY_KEYS) {
    let runStart: number | null = null;
    for (const h of HOUR_RANGE) {
      const on = !!set[`${day}-${h}`];
      if (on && runStart === null) runStart = h;
      if (!on && runStart !== null) {
        out[day].push({ start: `${String(runStart).padStart(2, '0')}:00`, end: `${String(h).padStart(2, '0')}:00` });
        runStart = null;
      }
    }
    if (runStart !== null) {
      const last = HOUR_RANGE[HOUR_RANGE.length - 1] + 1;
      out[day].push({ start: `${String(runStart).padStart(2, '0')}:00`, end: `${String(last).padStart(2, '0')}:00` });
    }
  }
  return out;
}

// Frontend admin tier mirrors what the backend saveBrokerAvailability
// action accepts for cross-broker writes (super_admin + administrator
// only — see services/client-functions/src/leads/leadCaptureAction.ts:
// 1078-1090). office_admin / marketing are NOT included here so they
// don't enter override mode and then hit a permission-denied error on
// save. Widening the backend gate to include those roles is a separate
// follow-up if product wants it.
const ADMIN_TIER_ROLES = new Set(['super_admin', 'administrator']);

const MyAvailabilityPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const ownUid = userData?.uid ?? '';
  const ownRole = userData?.role ?? '';

  // Admin override mode: ?broker=<uid> in the URL + caller is admin-tier
  // → edit that broker's availability instead of own. Non-admins fall
  // through silently (param ignored). Empty string + own-uid match also
  // fall through (no banner, no override).
  const requestedBrokerUid = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('broker');
    return v && v.trim() ? v.trim() : null;
  }, []);
  const isAdminOverride =
    !!requestedBrokerUid &&
    requestedBrokerUid !== ownUid &&
    ADMIN_TIER_ROLES.has(ownRole);
  const brokerUid = isAdminOverride ? (requestedBrokerUid as string) : ownUid;

  // One-shot read of the target broker's display name for the override
  // banner. No new hook — single use case.
  const [overrideTargetName, setOverrideTargetName] = useState<string | null>(null);
  useEffect(() => {
    if (!isAdminOverride || !companyId || !brokerUid) {
      setOverrideTargetName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // User profiles are a TOP-LEVEL collection. This read used to point at
        // companies/{companyId}/users/{uid}, which does not exist, so the
        // banner always fell back to showing a raw UID. Admin-tier callers may
        // read a user doc in their own company (firestore.rules /users/{userId}).
        const snap = await getDoc(doc(db, `users/${brokerUid}`));
        if (cancelled) return;
        const d = snap.data() as { displayName?: string; email?: string } | undefined;
        setOverrideTargetName(d?.displayName || d?.email || brokerUid);
      } catch {
        if (!cancelled) setOverrideTargetName(brokerUid);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdminOverride, companyId, brokerUid]);

  const [docState, setDocState] = useState<AvailabilityDoc>(DEFAULT_DOC);
  const [activeSet, setActiveSet] = useState<Record<string, boolean>>(() =>
    scheduleToActiveSet(DEFAULT_NINE_TO_FIVE),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // null while loading, then whether a saved document actually exists. A
  // broker who has never saved is shown a 9-5 grid, but the router treats a
  // missing document as available at ALL times, so the page has to say so
  // rather than implying the grid is in force.
  const [docExists, setDocExists] = useState<boolean | null>(null);
  // Set the moment the broker edits anything, cleared on save. A live snapshot
  // must never overwrite work in progress: a colleague saving an admin
  // override, or the broker's own save echoing back, used to wipe the grid
  // mid-edit with no warning.
  const dirty = useRef(false);
  const markDirty = useCallback(() => { dirty.current = true; }, []);

  // ── Live load from Firestore ───────────────────────────────────────
  useEffect(() => {
    if (!companyId || !brokerUid) {
      setLoading(false);
      return;
    }
    dirty.current = false;
    const ref = doc(
      db,
      `companies/${companyId}/brokers/${brokerUid}/availability/default`,
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setDocExists(snap.exists());
        setLoading(false);
        // Keep unsaved edits. The document state on screen is the broker's,
        // not the server's, until they press Save.
        if (dirty.current) return;
        if (!snap.exists()) {
          setDocState(DEFAULT_DOC);
          setActiveSet(scheduleToActiveSet(DEFAULT_NINE_TO_FIVE));
          return;
        }
        const data = snap.data() as Partial<AvailabilityDoc> | undefined;
        const saved = data?.timezone;
        const merged: AvailabilityDoc = {
          weeklySchedule: data?.weeklySchedule ?? EMPTY_SCHEDULE,
          timezone: isAuTimezone(saved) ? saved : 'Australia/Sydney',
          outOfOffice: data?.outOfOffice ?? { enabled: false, until: null, reason: null },
          maxOpenLeads: data?.maxOpenLeads ?? null,
          acceptingNewLeads: data?.acceptingNewLeads ?? true,
        };
        setDocState(merged);
        setActiveSet(scheduleToActiveSet(merged.weeklySchedule));
      },
      (err) => {
        console.error('[MyAvailability] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [companyId, brokerUid]);

  // ── Save ────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!companyId || !brokerUid) return;
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, { success: boolean }>(
        functions,
        'leadCaptureAction',
      );
      await fn({
        action: 'saveBrokerAvailability',
        companyId,
        brokerUid,
        availability: {
          ...docState,
          weeklySchedule: activeSetToSchedule(activeSet),
        },
      });
      dirty.current = false;
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [companyId, brokerUid, docState, activeSet]);

  // ── Calendar grid handlers ─────────────────────────────────────────
  const toggleSlot = useCallback((day: DayKey, hour: number) => {
    markDirty();
    setActiveSet((prev) => ({ ...prev, [`${day}-${hour}`]: !prev[`${day}-${hour}`] }));
  }, [markDirty]);

  const resetToNineFive = useCallback(() => {
    markDirty();
    setActiveSet(scheduleToActiveSet(DEFAULT_NINE_TO_FIVE));
  }, [markDirty]);

  /** Edit the availability document and mark the form dirty in one step. */
  const editDoc = useCallback((fn: (s: AvailabilityDoc) => AvailabilityDoc) => {
    markDirty();
    setDocState(fn);
  }, [markDirty]);

  // ── Status ─────────────────────────────────────────────────────────
  // Everything below is judged in the broker's SAVED timezone, not the
  // browser's. An admin in Sydney opening a Perth broker's page, or a broker
  // travelling, previously saw a pill computed from whatever clock the device
  // happened to be on.
  const status = useMemo((): { label: string; detail: string; tone: string } => {
    const AMBER = '#fcd34d';
    const GREEN = '#6ee7b7';
    if (loading || docExists === null) {
      return { label: 'Loading', detail: 'Reading your saved schedule', tone: AMBER };
    }
    // No saved document: the router does NOT apply the grid below, it treats
    // this broker as available around the clock. Say that plainly.
    if (!docExists) {
      return {
        label: 'Always on',
        detail: 'Leads can arrive at any hour',
        tone: AMBER,
      };
    }
    if (docState.outOfOffice.enabled) {
      const end = leaveEndMs(docState.outOfOffice.until, docState.timezone);
      if (end === null || end > Date.now()) {
        return { label: 'Offline', detail: 'Out of office', tone: AMBER };
      }
    }
    if (!docState.acceptingNewLeads) {
      return { label: 'Offline', detail: 'Paused: not accepting new leads', tone: AMBER };
    }
    const { dayKey, hour } = zonedDayAndHour(new Date(), docState.timezone);
    const working = !!activeSet[`${dayKey}-${hour}`];
    return working
      ? { label: 'Online', detail: 'Within working hours', tone: GREEN }
      : { label: 'Offline', detail: 'Outside working hours', tone: AMBER };
  }, [activeSet, docState, docExists, loading]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Clock}
        title="My Availability"
        subtitle="Set your working hours so you only get leads when you're online. Brokers and admins manage this together — admins can override for time-off coverage."
        badge="Beta"
        actions={
          <button
            onClick={save}
            disabled={saving || loading}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        }
      />

      {isAdminOverride && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-200 text-sm flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
          <div className="flex-1">
            <div className="font-medium text-amber-100">
              Editing availability for {overrideTargetName ?? '…'} — admin override mode
            </div>
            <div className="text-[12px] text-amber-300/80 mt-0.5">
              Changes are saved against this broker's profile and audit-logged.
            </div>
          </div>
          <a
            href="/leads/availability"
            className="text-xs text-amber-200 hover:text-white underline whitespace-nowrap"
          >
            Back to my availability
          </a>
        </div>
      )}

      {docExists === false && !loading && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 text-blue-100 text-sm flex items-start gap-3">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-300" />
          <div>
            <div className="font-medium">No schedule saved yet.</div>
            <div className="text-[12px] text-blue-200/80 mt-0.5">
              Until you save one, leads can be assigned to you at any time, including
              overnight and at weekends. The hours below are a starting suggestion and
              are not in force yet. Check the timezone, then press Save Changes.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {savedAt && Date.now() - savedAt < 3000 && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">
          Saved.
        </div>
      )}

      {/* Status row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Status</div>
          <div className="mt-1 text-lg font-semibold" style={{ color: status.tone }}>
            ● {status.label}
          </div>
          <div className="mt-1 text-[11px] text-gray-400">{status.detail}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Accepting Leads</div>
          <div className="mt-1 text-lg font-semibold text-white">{docState.acceptingNewLeads ? 'Yes' : 'No'}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Max Open Leads</div>
          <div className="mt-1 text-lg font-semibold text-white">{docState.maxOpenLeads ?? '— inherits'}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <label className="block text-[11px] uppercase tracking-wider text-gray-500" htmlFor="availability-timezone">
            Timezone
          </label>
          <select
            id="availability-timezone"
            value={docState.timezone}
            onChange={(e) => editDoc((s) => ({ ...s, timezone: e.target.value }))}
            className="mt-1 w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
          >
            {AU_TIMEZONE_VALUES.map((tz) => (
              <option key={tz} value={tz}>{AU_TIMEZONE_LABELS[tz]}</option>
            ))}
          </select>
          <div className="mt-1 text-[11px] text-gray-400">
            Your working hours are judged in this zone.
          </div>
        </div>
      </div>

      {/* Weekly schedule */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-base font-semibold text-white">Weekly Schedule</h3>
            <p className="text-xs text-gray-400 mt-1">
              Click cells to toggle when you&apos;re working. Times are in <strong>{docState.timezone}</strong>.
            </p>
          </div>
          <button onClick={resetToNineFive} className="px-3 py-1.5 text-xs rounded-md border border-gray-700 hover:bg-gray-800 text-gray-300">
            Reset to 9-5
          </button>
        </div>

        <div className="grid gap-px bg-gray-700 rounded-md overflow-hidden text-[11px]" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          <div className="bg-gray-900/60" />
          {DAY_KEYS.map((d) => (
            <div key={d} className="bg-gray-800/60 px-1 py-1.5 text-center text-gray-300 uppercase tracking-wider font-semibold">
              {DAY_LABELS[d]}
            </div>
          ))}
          {HOUR_RANGE.map((h) => (
            <React.Fragment key={h}>
              <div className="bg-black/40 text-right pr-2 py-0.5 text-gray-500">
                {h === 12 ? '12pm' : (h < 12 ? `${h}am` : `${h - 12}pm`)}
              </div>
              {DAY_KEYS.map((d) => {
                const on = !!activeSet[`${d}-${h}`];
                return (
                  <button
                    key={`${d}-${h}`}
                    onClick={() => toggleSlot(d, h)}
                    className={`bg-gray-900/40 hover:bg-blue-500/30 transition-colors min-h-[18px] ${on ? 'bg-blue-600' : ''}`}
                    aria-pressed={on}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* OOO + capacity row */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
          <h3 className="text-base font-semibold text-white mb-1">Out of Office</h3>
          <p className="text-xs text-gray-400 mb-4">Override your weekly schedule when you&apos;re on leave. While enabled, no new leads are assigned to you.</p>
          <label className="flex items-center justify-between py-2 border-b border-gray-800 text-sm">
            <span>OOO mode enabled</span>
            <input
              type="checkbox"
              checked={docState.outOfOffice.enabled}
              onChange={(e) =>
                editDoc((s) => ({ ...s, outOfOffice: { ...s.outOfOffice, enabled: e.target.checked } }))
              }
              className="accent-blue-500 w-4 h-4"
            />
          </label>
          <label className="flex items-center justify-between py-2 border-b border-gray-800 text-sm">
            <span>Until</span>
            <input
              type="date"
              value={docState.outOfOffice.until ?? ''}
              onChange={(e) =>
                editDoc((s) => ({ ...s, outOfOffice: { ...s.outOfOffice, until: e.target.value || null } }))
              }
              className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
            />
          </label>
          <label className="flex items-center justify-between py-2 text-sm">
            <span>Reason (admin-visible)</span>
            <input
              type="text"
              maxLength={200}
              placeholder="e.g. Annual leave"
              value={docState.outOfOffice.reason ?? ''}
              onChange={(e) =>
                editDoc((s) => ({ ...s, outOfOffice: { ...s.outOfOffice, reason: e.target.value || null } }))
              }
              className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs w-1/2"
            />
          </label>
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
          <h3 className="text-base font-semibold text-white mb-1">Capacity & Pause</h3>
          <p className="text-xs text-gray-400 mb-4">Soft-cap your active workload. New leads roll to the next broker when you&apos;re at capacity or paused.</p>
          <label className="flex items-center justify-between py-2 border-b border-gray-800 text-sm">
            <span>Accepting new leads</span>
            <input
              type="checkbox"
              checked={docState.acceptingNewLeads}
              onChange={(e) => editDoc((s) => ({ ...s, acceptingNewLeads: e.target.checked }))}
              className="accent-blue-500 w-4 h-4"
            />
          </label>
          <label className="flex items-center justify-between py-2 text-sm">
            <span>Max active leads (blank = inherit)</span>
            <input
              type="number"
              min={1}
              max={500}
              value={docState.maxOpenLeads ?? ''}
              onChange={(e) =>
                editDoc((s) => ({ ...s, maxOpenLeads: e.target.value ? parseInt(e.target.value, 10) : null }))
              }
              className="bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs w-24"
            />
          </label>
        </div>
      </div>

      {/* Calendar connection. Own calendar only, so it is hidden in admin
          override mode: an admin cannot consent to Google on someone else's
          behalf. Self-contained; it owns its own loading and error states. */}
      {!isAdminOverride && <CalendarConnectionCard companyId={companyId} />}

      {loading && (
        <div className="text-center text-gray-400 text-sm py-4">Loading availability…</div>
      )}
    </div>
  );
};

export default MyAvailabilityPage;
